import styled from 'styled-components'
import { authForm } from '../../pages/auth-card'
import { signInForm } from '../SignInForm/styles'
import { signUpFields } from '../SignUpForms/styles'

export default styled.div`
  position: relative;
  width: 380px;
  max-width: 100%;
  /* The card is the scrolling box, not the modal frame: a rounded frame that clips would keep
     clicks from reaching Google's sign in iframe (see FRAME_STYLE). */
  max-height: 90vh;
  overflow-y: auto;
  /* The corner buttons end at 44px; the content starts a clear step below them. */
  padding: 64px 32px 32px;
  box-sizing: border-box;

  /* Back and close sit in the top corners, outside the flow, same size, black as in the mock. */
  > .corner {
    position: absolute;
    top: 20px;
    width: 24px;
    height: 24px;
    padding: 4px;
    border: none;
    background: none;
    color: ${ ({ theme }) => theme.colors.black };
    cursor: pointer;

    svg {
      width: 100%;
      height: 100%;
      display: block;
    }

    &:hover {
      color: ${ ({ theme }) => theme.colors.primary };
    }

    &.close {
      right: 20px;
    }

    &.back {
      left: 20px;
    }
  }

  /* Sign up progress on the corner buttons' row: numbered dots joined by a line, the current one filled. */
  > .steps {
    position: absolute;
    top: 20px;
    left: 64px;
    right: 64px;
    height: 24px;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    list-style: none;
    color: ${ ({ theme }) => theme.colors.primary };

    /* One line behind the three dots, from the centre of the first to the centre of the last
       (3 dots of 26px, 14px apart: 106px wide, so 40px either side of the middle). The dots are
       opaque and sit above it, so it always reaches each circle and never paints over one. */
    &::before {
      content: '';
      position: absolute;
      left: calc(50% - 40px);
      width: 80px;
      top: 50%;
      height: 1px;
      margin-top: -1px;
      background-color: ${ ({ theme }) => theme.colors.primary };
    }

    li {
      position: relative;
      z-index: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 26px;
      height: 26px;
      border: 1.5px solid currentColor;
      border-radius: 50%;
      box-sizing: border-box;
      background-color: ${ ({ theme }) => theme.colors.white };
      font-size: 13px;
      font-weight: bold;
      line-height: 1;

      & + li {
        margin-left: 14px;
      }

      &.current {
        background-color: ${ ({ theme }) => theme.colors.primary };
        /* The number goes white, but the ring must stay brand coloured: a white ring would cut the line. */
        border-color: ${ ({ theme }) => theme.colors.primary };
        color: ${ ({ theme }) => theme.colors.white };
      }
    }
  }

  form {
    display: flex;
    flex-direction: column;
    align-items: stretch;

    /* Titled screens: the title centred between the two corner buttons. */
    header {
      display: flex;
      flex-direction: row;
      justify-content: center;
      margin-bottom: 28px;
      padding: 0 24px;
      text-align: center;

      .titles {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;

        h3 {
          margin: 0;
          font-size: 20px;
          font-weight: bold;
          color: ${ ({ theme }) => theme.colors.primary };
        }
      }

      /* Sign up steps: the step counter, the avatar and the title, all in the brand colour. */
      &.sign-up {
        margin-top: 4px;
        margin-bottom: 32px;
        color: ${ ({ theme }) => theme.colors.primary };

        .avatar {
          width: 64px;
          height: 64px;
          margin-bottom: 12px;
        }

        h3 {
          font-size: 24px;
        }
      }
    }

    /* Login: the brand's rounded logo and the title, centred, as on the sign in page. */
    .brand {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-bottom: 24px;

      img {
        width: 99px;
        height: 99px;
        margin-bottom: 24px;
      }

      img.logoAmb {
        width: auto;
        max-width: 260px;
        height: auto;
      }

      h3 {
        margin: 0;
        font-size: 24px;
        font-weight: bold;
        color: ${ ({ theme }) => theme.colors.primary };
      }
    }

    .intro {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      margin-bottom: 28px;
      color: ${ ({ theme }) => theme.colors.primary };

      .lock {
        width: 56px;
        height: 56px;
        margin-bottom: 16px;
      }

      h3 {
        margin: 0 0 4px;
        font-size: 22px;
        font-weight: bold;
        line-height: 28px;
      }

      p {
        margin: 0;
        font-size: 15px;
        line-height: 20px;
        color: ${ ({ theme }) => theme.colors.greyDark };
      }
    }

    .choices {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 12px;

      button {
        height: 48px;
        font-size: 14px;
        letter-spacing: 1px;
      }

      .google-pill {
        font-size: 13px;
        font-weight: 600;
        line-height: 19.5px;
        letter-spacing: 0.52px;
        text-align: center;
      }

      .divider {
        margin: 8px 0;
      }

      .stay-guest {
        align-self: center;
        margin-top: 4px;
        color: ${ ({ theme }) => theme.colors.primary };
        font-size: 14px;
        font-weight: bold;
        letter-spacing: 0.5px;
        cursor: pointer;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    ${ authForm }
    ${ signUpFields }
    ${ signInForm }

    /* No shared card height here: the button follows the fields. */
    .actions {
      margin-top: 0;
      padding-top: 8px;
    }

    /* The sign in form keeps its own rhythm: a little air before the button, none after. */
    .fields + .actions {
      padding-top: 16px;
    }

    /* "Already a member? Sign in.": plain dark text with the call to action in bold, no underline. */
    .actions .link {
      margin-top: 16px;

      span {
        color: ${ ({ theme }) => theme.colors.black };
        font-size: 13px;
        text-decoration: none;

        strong {
          font-weight: 700;
        }
      }
    }
  }

  @media screen and (max-width: 768px) {
    width: 90vw;
    padding: 60px 24px 24px;

    > .corner {
      top: 16px;

      &.close { right: 16px; }
      &.back { left: 16px; }
    }

    > .steps {
      top: 16px;
    }
  }
`
